import { MapPin, Clock, Phone } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { PHONE_HREF, PHONE_DISPLAY } from "@/lib/contact";

export default function MapSection() {
  const sectionRef = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setRevealed(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      data-testid="map-section"
      className="py-28 md:py-36 bg-[#f8f6f3]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        {/* Header */}
        <div className={`text-center mb-14 transition-all duration-700 ${revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <span className="text-[#c9a96e] text-[10px] uppercase tracking-[0.3em] font-semibold" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Ubicación
          </span>
          <h2
            data-testid="map-title"
            className="text-3xl sm:text-4xl md:text-[3.25rem] font-light tracking-tight mt-5 text-[#1a1a1a] leading-[1.15]"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Dónde encontrarnos
          </h2>
          <div className="w-12 h-[2px] bg-[#c9a96e] mx-auto mt-7" />
        </div>

        {/* Map + Info */}
        <div className={`grid lg:grid-cols-3 gap-6 transition-all duration-700 delay-200 ${revealed ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {/* Google Maps iframe */}
          <div className="lg:col-span-2 rounded-sm overflow-hidden border border-[#e0dcd6] h-80 md:h-[420px]">
            <iframe
              title="Ubicación A.F. Puertos"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              loading="lazy"
              allowFullScreen
              src="https://maps.google.com/maps?q=Calle+Gerald+Brenan,+39,+29120+Alhaurin+el+Grande,+Malaga&output=embed&z=16"
            />
          </div>

          {/* Info panel */}
          <div className="flex flex-col justify-center gap-8 bg-white rounded-sm border border-[#e0dcd6] p-8">
            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="w-4 h-4 text-[#c9a96e] flex-shrink-0" />
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#c9a96e] font-semibold" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  Dirección
                </span>
              </div>
              <p className="text-[#1a1a1a] font-light leading-relaxed text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Calle Gerald Brenan, 39, 1B<br />
                29120 Alhaurín el Grande<br />
                Málaga, España
              </p>
            </div>

            <div className="h-[1px] bg-[#e8e4de]" />

            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-4 h-4 text-[#c9a96e] flex-shrink-0" />
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#c9a96e] font-semibold" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  Horario
                </span>
              </div>
              <p className="text-[#1a1a1a] font-light text-sm leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Lunes a Viernes<br />
                9:00 – 14:00<br />
                Tardes con cita previa
              </p>
            </div>

            <div className="h-[1px] bg-[#e8e4de]" />

            <div className="space-y-2">
              <div className="flex items-center gap-2 mb-4">
                <Phone className="w-4 h-4 text-[#c9a96e] flex-shrink-0" />
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#c9a96e] font-semibold" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  Teléfono
                </span>
              </div>
              <a
                href={PHONE_HREF}
                className="text-[#1a1a1a] hover:text-[#c9a96e] transition-colors duration-300 font-light text-sm"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
